import React, { useState, useMemo, useRef, useCallback } from 'react'
import { useDrag } from 'react-dnd'
import { TYPE } from '../../goods-library'
import Button from '@/components/button/button'
import { Goods } from '@/model/types/goods'

import './goods-table.scss'
import { Table, Tooltip, Popover } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { getBEMCls } from '@/utils/classname'
import { Row } from '@/components/layout/layout'
import Iconfont from '@/components/iconfont/iconfont'
import { showLayer, layerManage } from '@/utils/layer'
import { $post } from '@/api'
import { useLocalStorageState, useRequest, useSelections, useUpdateEffect } from 'ahooks'
import { GoodsDetail } from '../goods-detail/goods-detail'
import { PricesPopper } from '@/views/main/modules/goods-manage/components/prices/prices-popper/prices-popper'
import { EditGoods } from '../edit-goods/edit-goods'
import { goodsModel } from '../edit-goods/modules/model'
import { Modal } from '@/components/dialog/modal'
import $msg from '@/utils/message'
import { GoodsLibraryStore, goodsLibraryStore } from '../../store/goods-library'
import { observer } from 'mobx-react'
import { AutoResizer, WithScrollLoad } from '@/utils/utils-widget'
import { IPagination } from '@/model/types/common'
import { intervalPrice } from '@/utils/goods'
import EditPackage from '../edit-package/edit-package'
import { get, errorCallBack } from '@/utils'
import Auth from '@/views/main/components/auth/auth'
import { shopStore } from '@/store/shop'
import useSetDataTogether from '@/utils/hooks/useSetDataTogether'
import classnames from 'classnames'
import BatchShelf from './batch-shelf/batch-shelf'
import BatchGoodPrice from './batch-price/batch-price'
import BatchPriceCustom from './batch-price/batch-price-custom'
import { getQiniuImgUrl } from '@/config/qiniu'
import { GoodTableSortContainer } from './goods-sort/goods-sort'
import { useTableSort } from './goods-sort/useTableSort'
import TextPopover from '@/components/text-popover/text-popover'

interface GoodsTableProps {
    goodsLibraryStore: GoodsLibraryStore
}

export interface GoodsTableList {
    list: Goods[]
    pagination: IPagination
}

const GoodsTable: React.FC<GoodsTableProps> = observer(props => {
    const cls = (...args: string[]) => getBEMCls('goods-table', ...args)

    const [previewList, setPreviewList] = useState<Goods[]>([])
    const [selectKeys, setSelectKeys] = useState<number[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)
    const setAllData = useSetDataTogether()

    const [visable, setVisable] = useState<boolean>(false)
    const [activePopverIndex, setActivePopverIndex] = useState<number>(-1)

    const grouIdsAction = useSelections<number>([])
    const secondGrouIdsAction = useSelections<number>([])
    const [imgCheck, setImgCheck] = useLocalStorageState<boolean>('GOOD_TABLE_IMG_CHECK', true)

    const groupToGoodIdsMap = useRef<Record<string, number[]>>({})
    const secondGroupToGoodIdsMap = useRef<Record<string, number[]>>({})

    const { data: goodIdsData, loading: goodIdLoading } = useRequest<any>(
        () =>
            props.goodsLibraryStore.groupId !== undefined
                ? $post({
                      url: '/goods/getgroupgoodsids',
                      params: {
                          group_id: props.goodsLibraryStore.groupId ? props.goodsLibraryStore.groupId : undefined,
                          secondary_group_id: props.goodsLibraryStore.secondGroupId,
                      },
                  })
                : new Promise(resolve => {
                      resolve({
                          goods_ids: [],
                      })
                  }),
        {
            refreshDeps: [props.goodsLibraryStore.groupId, props.goodsLibraryStore.secondGroupId],
            onSuccess: res => {
                if (goodsLibraryStore.clearSelectKey) {
                    setAllData(() => {
                        setSelectKeys([])
                        setPreviewList([])
                        setSelectAll(false)
                        grouIdsAction.unSelectAll()
                        secondGrouIdsAction.unSelectAll()
                    })
                    goodsLibraryStore.setClearSelectKey(false)
                }
            },
        },
    )

    const [, drag, preview] = useDrag({
        item: {
            type: TYPE,
            goods_ids: selectKeys,
        },
        canDrag: () => !!selectKeys.length,
    })

    const isAllGoods = useMemo(() => {
        return !props.goodsLibraryStore.groupId && !props.goodsLibraryStore.groupIds.length
    }, [props.goodsLibraryStore.groupId, props.goodsLibraryStore.groupIds.length])

    const requestHandle = useRequest<
        GoodsTableList,
        {
            goods_list: Goods[]
            pagination: IPagination
        }
    >(
        val => {
            const secondId = props.goodsLibraryStore.secondGroupId

            return (
                // 在分组未完成加载前不加载表格数据
                props.goodsLibraryStore.groupId !== undefined
                    ? $post({
                          url: '/goods/getgoods',
                          params: {
                              // 如果当前分组不是全部商品，则获取当前分组的商品，如果是全部商品，判断是否进行了分组筛选，都没有就获取全部的商品
                              group_ids: props.goodsLibraryStore.groupId
                                  ? [props.goodsLibraryStore.groupId]
                                  : props.goodsLibraryStore.groupIds.length
                                  ? props.goodsLibraryStore.groupIds
                                  : undefined,
                              secondary_group_id: secondId,
                              hide_off_shelf_goods: shopStore.shopConfig?.hide_off_shelf_goods,
                              limit: 20,
                              page: val?.pagination?.page ? val.pagination.page + 1 : 1,
                              //   status: props.goodsLibraryStore.status ? 0 : undefined,
                              keyword: props.goodsLibraryStore.keyWord ? props.goodsLibraryStore.keyWord : undefined,
                          },
                      })
                    : new Promise(() => {})
            )
        },
        {
            isNoMore: data => {
                if (data?.pagination) {
                    return data?.pagination.page === data?.pagination.page_count
                } else {
                    return false
                }
            },
            onError: e => $msg('error', e.message),
            loadMore: true,
            formatResult: data => {
                return {
                    list: data.goods_list,
                    pagination: data.pagination,
                }
            },
            refreshDeps: [
                shopStore.shopConfig,
                props.goodsLibraryStore.groupId,
                props.goodsLibraryStore.secondGroupId,
                props.goodsLibraryStore.keyWord,
                props.goodsLibraryStore.groupIds,
                props.goodsLibraryStore.status,
                props.goodsLibraryStore.resetSatus, // 用于重置数据
                props.goodsLibraryStore.updateGroupStatus, // 用于刷新当前分组数据
            ],
        },
    )

    const { data, loading, refresh } = requestHandle

    const filterGroupData = useMemo(() => {
        return goodsModel.goodsGroup.map(group => ({
            text: group.name,
            value: group.id || false,
            children: group.secondary_group.length
                ? group.secondary_group.map(second => ({
                      text: second.name,
                      value: second.id || false,
                  }))
                : undefined,
        }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [goodsModel.goodsGroup])

    const tableDefaultColumn = useMemo<ColumnsType<Goods>>(() => {
        return [
            {
                title: '所属分组',
                key: 'group',
                dataIndex: 'group',
                width: 140,
                render: value => (
                    <div
                        className="text-ellipsis-one"
                        style={{
                            maxWidth: '130px',
                        }}
                    >
                        {value.name}
                    </div>
                ),
                filters: !props.goodsLibraryStore.groupId ? filterGroupData : void 0,
                onFilter: (value, record) => {
                    return record.group.id === value || record.secondary_group.id === value
                },
            },
            {
                title: '所属类目',
                key: 'category',
                dataIndex: 'category',
                width: 110,
                render: category => category.name,
            },
            {
                title: '商品编码',
                key: 'goods_code',
                dataIndex: 'goods_code',
                width: 120,
                ellipsis: true,
            },
            {
                title: '商品条码',
                key: 'price_code',
                width: 120,
                render: (v, r) => (
                    <div
                        className="two-line"
                        style={{
                            maxWidth: '110px',
                        }}
                    >
                        {r.pricing_methods
                            .map(p => p.price_code)
                            .filter(Boolean)
                            .join('、')}
                    </div>
                ),
            },
            {
                title: '供货商',
                key: 'suppliers',
                width: 120,
                ellipsis: true,
                render: (val, r) => (
                    <div
                        className="two-line"
                        style={{
                            maxWidth: '90px',
                        }}
                    >
                        {r.suppliers.map(s => s.name).join('、')}
                    </div>
                ),
            },
            {
                title: '售价',
                dataIndex: 'pricing_methods',
                key: 'pricing_methods',
                align: 'right',
                width: 100,
                render: (pricingMethod, record) => (
                    <div>
                        {record.pricing_methods.length > 1 || record.pricing_methods[0].sale_prices.length > 1 ? (
                            <Tooltip
                                overlayClassName="custom-popover"
                                arrowPointAtCenter={false}
                                title={() => (
                                    <PricesPopper
                                        stockUnit={record.stock_unit}
                                        pricingMethod={pricingMethod}
                                    ></PricesPopper>
                                )}
                            >
                                <div data-cursor-pointer>
                                    ¥{intervalPrice(record.pricing_methods).min}~
                                    {intervalPrice(record.pricing_methods).max}
                                </div>
                            </Tooltip>
                        ) : (
                            <div>
                                ¥{get(record, 'pricing_methods[0].sale_prices[0].sale_price', '')}/
                                {record.pricing_methods[0].sale_prices[0].sale_unit.name}
                            </div>
                        )}
                    </div>
                ),
            },
            {
                title: '上架',
                key: 'scene',
                width: 80,
                render: (value, record) => (
                    <Row>
                        {record.status === 0 || record.on_mall ? (
                            <React.Fragment>
                                <span className={cls('icon')}>
                                    {record.on_mall && <Iconfont name="icon2shangjiaweishangcheng" size={20} />}
                                </span>
                                <span>
                                    {record.status === 0 && (
                                        <Iconfont
                                            name="icon2shangjiashouyintai"
                                            size={20}
                                            style={{
                                                marginLeft: '8px',
                                            }}
                                        />
                                    )}
                                </span>
                            </React.Fragment>
                        ) : (
                            <span>
                                <Iconfont name="iconweishangjia1" size={35}></Iconfont>
                            </span>
                        )}
                    </Row>
                ),
            },
        ]
    }, [filterGroupData, props.goodsLibraryStore.groupId])

    const { sortData, sortColumn, setSortData } = useTableSort<Goods>({
        key: `GOODS_TABLE_SORT_DATA`,
        defaultValue: tableDefaultColumn,
    })

    const previewColumns = useMemo<ColumnsType<Goods>>(() => {
        return [
            {
                title: '#',
                key: 'index',
                width: 50,
                fixed: 'left',
                render: (value, record, index) => <span id="drag">{index + 1}</span>,
            },
            {
                title: '图片',
                key: 'index',
                width: 60,
                align: 'center',
                dataIndex: 'img_url',
                fixed: 'left',
                render: (value, record) => (
                    <img
                        src={getQiniuImgUrl(record.img_url)}
                        alt=""
                        width="40"
                        height="40"
                        data-border-radius-5
                        data-margin-top-8
                    />
                ),
            },
            {
                title: '商品名',
                dataIndex: 'name',
                width: 240,
                fixed: 'left',
                key: 'goodName',
                render: (value, record) => (
                    <Row data-align-items-center>
                        {!!record.goods_package.length && <div className={cls('package')}>套</div>}
                        <TextPopover
                            title={value}
                            width={250}
                            style={{
                                color: '#333',
                                fontWeight: 600,
                            }}
                            keyWord={props.goodsLibraryStore.keyWord}
                        />
                    </Row>
                ),
            },
            {
                title: '所属类目',
                key: 'category',
                dataIndex: 'category',
                width: 110,
                render: category => category.name,
            },
            {
                title: '商品编码',
                key: 'goods_code',
                dataIndex: 'goods_code',
                width: 120,
                ellipsis: true,
            },
        ]
    }, [props.goodsLibraryStore.keyWord])

    const columns = useMemo<ColumnsType<Goods>>(() => {
        const tmp: ColumnsType<Goods> = [
            {
                title: '#',
                key: 'index',
                width: 50,
                fixed: 'left',
                render: (value, record, index) => <span id="drag">{index + 1}</span>,
            },
            {
                title: '商品名',
                dataIndex: 'name',
                width: 290,
                fixed: 'left',
                key: 'goodName',
                ellipsis: {
                    showTitle: true,
                },
                render: (value, record) => (
                    <Row data-align-items-center>
                        {!!record.goods_package.length && <div className={cls('package')}>套</div>}
                        <TextPopover
                            title={value}
                            width={250}
                            style={{
                                color: '#333',
                                fontWeight: 600,
                            }}
                            keyWord={props.goodsLibraryStore.keyWord}
                        />
                    </Row>
                ),
            },
            ...sortColumn,
            {
                title: (
                    <Row
                        data-align-items-center
                        data-justify-content-space-between
                        data-width-100p
                        data-cursor-pointer
                        onClick={() => {
                            showLayer(
                                <GoodTableSortContainer
                                    checkImg={!!imgCheck}
                                    onSortEnd={(data, check) => {
                                        setImgCheck(check)
                                        setSortData(data)
                                    }}
                                    sortData={sortData}
                                />,
                            )
                        }}
                    >
                        <div>操作</div>
                        <Iconfont name="iconshezhi2" size={16} />
                    </Row>
                ),
                key: 'action',
                width: 114,
                align: 'center',
                fixed: 'right',
                render: (value, record) => (
                    <>
                        <div
                            className={cls('action')}
                            onClick={() => {
                                showLayer(
                                    <GoodsDetail
                                        goodsId={record.id}
                                        onChange={() => {
                                            refresh()
                                        }}
                                    ></GoodsDetail>,
                                )
                            }}
                        >
                            详情
                        </div>
                        <div
                            className={cls('action')}
                            style={{
                                marginLeft: '18px',
                            }}
                            onClick={() => {
                                if (record.chain_goods) return $msg('warning', '连锁商品编辑请前往零售通APP操作')
                                showLayer(
                                    !record.goods_package.length ? (
                                        <EditGoods goodsModel={goodsModel} mode="edit" goodsId={record.id} />
                                    ) : (
                                        <EditPackage mode="edit" packageId={record.id} goodsModel={goodsModel} />
                                    ),
                                )
                            }}
                        >
                            编辑
                        </div>
                    </>
                ),
            },
        ]

        if (imgCheck) {
            tmp.splice(1, 0, {
                title: '图片',
                key: 'index',
                width: 60,
                align: 'center',
                dataIndex: 'img_url',
                fixed: 'left',
                render: (value, record) => (
                    <img
                        src={getQiniuImgUrl(record.img_url)}
                        alt=""
                        width="40"
                        height="40"
                        data-border-radius-5
                        data-margin-top-8
                    />
                ),
            })
        }

        return tmp
    }, [imgCheck, props.goodsLibraryStore.keyWord, refresh, setImgCheck, setSortData, sortColumn, sortData])

    const delteGoods = () => {
        $post({
            url: '/goods/onekeydeletegoods',
            params: getBatchOperateData(),
        })
            .then(() => {
                $msg('success', '删除成功')
                goodsLibraryStore.setUpdateGroupStatus(!goodsLibraryStore.updateGroupStatus)
            })
            .catch(e => $msg('error', e.message))
    }

    useUpdateEffect(() => {
        if (selectAll) {
            if (props.goodsLibraryStore.secondGroupId) {
                secondGroupToGoodIdsMap.current[props.goodsLibraryStore.secondGroupId] =
                    (goodIdsData as any)?.goods_ids || []
                secondGrouIdsAction.select(props.goodsLibraryStore.secondGroupId)
            } else {
                if (props.goodsLibraryStore.groupId) {
                    groupToGoodIdsMap.current[props.goodsLibraryStore.groupId] = goodIdsData?.goods_ids || []
                    grouIdsAction.select(props.goodsLibraryStore.groupId)
                }
            }
        } else {
            if (props.goodsLibraryStore.secondGroupId) {
                secondGroupToGoodIdsMap.current[props.goodsLibraryStore.secondGroupId] = []
                secondGrouIdsAction.unSelect(props.goodsLibraryStore.secondGroupId)
            } else {
                if (props.goodsLibraryStore.groupId) {
                    grouIdsAction.unSelect(props.goodsLibraryStore.groupId)
                    groupToGoodIdsMap.current[props.goodsLibraryStore.groupId] = []
                }
            }
        }
    }, [selectAll])

    const allGoodIds = useMemo(() => {
        if (!goodIdsData?.goods_ids) {
            return []
        } else {
            return goodIdsData?.goods_ids
        }
    }, [goodIdsData])

    const getBatchOperateData = useCallback(() => {
        const goods_group_ids: number[] = []
        const goods_secondary_group_ids: number[] = []
        let filterIds: number[] = []
        Object.keys(groupToGoodIdsMap.current).forEach(key => {
            if (groupToGoodIdsMap.current[key].length) {
                goods_group_ids.push(+key)
            }
            filterIds = [...filterIds, ...groupToGoodIdsMap.current[key]]
        })
        Object.keys(secondGroupToGoodIdsMap.current).forEach(key => {
            if (secondGroupToGoodIdsMap.current[key].length) {
                goods_secondary_group_ids.push(+key)
            }
            filterIds = [...filterIds, ...secondGroupToGoodIdsMap.current[key]]
        })

        return {
            goods_list: selectKeys.filter(v => !filterIds.includes(v)),
            goods_group_ids,
            goods_secondary_group_ids,
        }
    }, [selectKeys])

    const stockSettingReq = (allow: boolean) => {
        $post({
            url: '/goods/batcheditgoods',
            params: {
                ...getBatchOperateData(),
                negative_stock_mode: allow ? 0 : 2,
            },
        })
            .then(() => {
                $msg('success', '处理成功')
                goodsLibraryStore.setUpdateGroupStatus(!goodsLibraryStore.updateGroupStatus)
            })
            .catch(errorCallBack)
    }

    const stockSettingConfirm = (allow: boolean) => {
        showLayer(
            <Modal
                headerStyles={{ width: '440px' }}
                isShow
                type="danger"
                style={{ height: '136px' }}
                onClose={() => layerManage.pop()}
                onCancel={() => layerManage.pop()}
                onConfirm={() => {
                    layerManage.pop()
                    stockSettingReq(allow)
                }}
            >
                <div>
                    <div data-font-size-20 data-font-weight-600 data-color-000>
                        确认
                        <span data-color-ff4343 data-margin-h-6>
                            全部{allow ? '' : '不'}允许{selectKeys.length}
                        </span>
                        个商品负库存吗？
                    </div>
                </div>
            </Modal>,
        )
    }

    const PopoverContentConfig = [
        {
            name: '批量上下架',
            onClick: () => {
                setVisable(false)
                showLayer(<BatchShelf {...getBatchOperateData()} />)
            },
        },
        {
            name: '批量改价',
            children: [
                {
                    name: '统一改价',
                    onClick: () => {
                        setVisable(false)
                        showLayer(<BatchGoodPrice goodIds={selectKeys} {...getBatchOperateData()} />)
                    },
                },
                {
                    name: '自定义改价',
                    onClick: () => {
                        showLayer(<BatchPriceCustom goodIds={selectKeys} />)
                        setVisable(false)
                    },
                },
            ],
        },
        {
            name: '负库存设置',
            children: [
                {
                    name: '允许负库存',
                    onClick: () => {
                        setVisable(false)
                        stockSettingConfirm(true)
                    },
                },
                {
                    name: '不允许负库存',
                    onClick: () => {
                        setVisable(false)
                        stockSettingConfirm(false)
                    },
                },
            ],
        },
        {
            name: '删除',
            color: '#FF4343',
            onClick: () => {
                setVisable(false)
                showLayer(
                    <Modal
                        headerStyles={{ width: '440px' }}
                        isShow
                        type="danger"
                        style={{ height: '156px' }}
                        onClose={() => layerManage.pop()}
                        onCancel={() => layerManage.pop()}
                        onConfirm={() => {
                            layerManage.pop()
                            delteGoods()
                        }}
                    >
                        <div>
                            <div data-font-size-20 data-font-weight-600 data-color-000>
                                确认删除选中的{selectKeys.length}种商品吗？
                            </div>
                            <div data-font-size-14 data-color-999 data-margin-top-8>
                                删除商品后，仍会保留其相关销售数据
                            </div>
                        </div>
                    </Modal>,
                )
            },
        },
    ]

    useUpdateEffect(() => {
        !visable && setActivePopverIndex(-1)
    }, [visable])

    return (
        <>
            <div ref={drag} className={cls('container')}>
                <AutoResizer>
                    {({ height }) => {
                        return (
                            <WithScrollLoad data-expaned data-overflow-hidden scrollloadhandle={requestHandle}>
                                <Table
                                    rowKey={record => record.id}
                                    loading={loading || requestHandle.loadingMore || void 0}
                                    rowSelection={{
                                        type: 'checkbox',
                                        hideSelectAll: goodIdLoading || isAllGoods,
                                        selectedRowKeys: selectKeys,
                                        onChange: (selectedRowKeys, selectedRows) => {
                                            // 记录单前分组选择的商品数量
                                            props.goodsLibraryStore.setSelectGroupNum(selectedRowKeys.length)

                                            setAllData(() => {
                                                setSelectAll(
                                                    selectedRowKeys.length === (goodIdsData as any)?.goods_ids.length,
                                                )
                                                setSelectKeys(vals => {
                                                    const freeVals = isAllGoods
                                                        ? []
                                                        : vals.filter(v => !allGoodIds.includes(v))

                                                    return [...freeVals, ...selectedRowKeys] as number[]
                                                })
                                                setPreviewList(l => {
                                                    const freeVals = isAllGoods
                                                        ? []
                                                        : l.filter(v => !allGoodIds.includes(v.id))

                                                    return [...freeVals, ...selectedRows]
                                                })
                                            })
                                        },
                                        onSelectAll: selected => {
                                            // 记录单前分组选择的商品数量
                                            props.goodsLibraryStore.setSelectGroupNum(selected ? allGoodIds.length : 0)

                                            setAllData(() => {
                                                setSelectAll(selected)
                                                setSelectKeys(vals => {
                                                    return selected
                                                        ? [...new Set([...vals, ...allGoodIds])]
                                                        : vals.filter(v => !(allGoodIds as number[]).includes(v))
                                                })
                                                setPreviewList(l => {
                                                    if (selected) {
                                                        if (data?.list) {
                                                            return [...l, ...data?.list]
                                                        } else {
                                                            return l
                                                        }
                                                    } else {
                                                        return l.filter(v => !allGoodIds.includes(v.id))
                                                    }
                                                })
                                            })
                                        },
                                    }}
                                    bordered
                                    pagination={false}
                                    columns={columns}
                                    dataSource={data?.list}
                                    scroll={{ x: 'max-content', y: height - 50 }}
                                />
                            </WithScrollLoad>
                        )
                    }}
                </AutoResizer>
                <Row className={cls('container', 'bottom')}>
                    <div data-font-size-14 data-margin-left-16>
                        已选
                        <div data-inline data-color-theme data-margin-h-4>
                            {selectKeys.length}
                        </div>
                        个商品
                    </div>

                    {!!selectKeys.length && (
                        <Popover
                            placement="topLeft"
                            visible={visable}
                            onVisibleChange={() => setVisable(!visable)}
                            content={
                                <Row>
                                    <ul data-padding-v-10>
                                        {PopoverContentConfig.map((i, index) => (
                                            <Auth key={i.name} perssion={index === 2 ? 25 : void 0}>
                                                <li
                                                    className={classnames(cls('popover', 'item'), {
                                                        [cls('popover', 'active')]: activePopverIndex === index,
                                                    })}
                                                    style={{
                                                        color: i.color,
                                                    }}
                                                    onMouseEnter={() => {
                                                        setActivePopverIndex(index)
                                                    }}
                                                    onClick={() => {
                                                        i.onClick && i.onClick()
                                                    }}
                                                >
                                                    {i.name}
                                                    {i?.children && <Iconfont name="icon6youjiantou-20" size={16} />}
                                                </li>
                                            </Auth>
                                        ))}
                                    </ul>
                                    {PopoverContentConfig[activePopverIndex]?.children && (
                                        <ul data-padding-v-10>
                                            {PopoverContentConfig[activePopverIndex].children!.map(i => (
                                                <li
                                                    key={i.name}
                                                    className={classnames(cls('popover', 'item'))}
                                                    onClick={() => {
                                                        i.onClick && i.onClick()
                                                    }}
                                                >
                                                    {i.name}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </Row>
                            }
                            trigger="click"
                        >
                            <div>
                                <Button
                                    types="more"
                                    style={{
                                        margin: '0 16px',
                                    }}
                                >
                                    批量处理
                                </Button>
                            </div>
                        </Popover>
                    )}
                </Row>
            </div>
            <div ref={preview} className={cls('preview')}>
                <Table
                    rowKey={record => record.id}
                    pagination={false}
                    bordered
                    columns={previewColumns}
                    dataSource={previewList.slice(0, 30)}
                />
                <div className={cls('badge')}>{selectKeys.length}</div>
            </div>
        </>
    )
})

export default GoodsTable
